import { useCallback, useEffect, useMemo, useState } from 'react';
import { CustomPdfScript, EditablePdfButtonGroup } from '@urbanx/agx-ui-components';
import {
  BrowserLoadedFile,
  exportAutomationFields,
  getCompleteAutomationLogicalNames,
  importAutomationFields,
  makeAutomationMappingFieldGroups,
  PdfEditorBrowserFileSource,
  PdfEditorState,
  useToast,
} from '@urbanx/agx.ui-components2';
import { preparePdfForEditing } from 'services';
import { makeAutomationConfiguration } from 'pdf/EditablePdfUpload/automationAdapter';

interface SellerDisclosurePdfOptions {
  buttonGroups: EditablePdfButtonGroup[];
  customScript: CustomPdfScript;
  requiredPdfFields: string[];
  getAuthToken: () => Promise<string | void>;
}

type ModalStep = 'mapping' | 'compliance';

export const useSellerDisclosurePdf = ({
  buttonGroups,
  customScript,
  requiredPdfFields,
  getAuthToken,
}: SellerDisclosurePdfOptions) => {
  const { pushToast } = useToast();
  const configuration = useMemo(
    () => makeAutomationConfiguration(buttonGroups, customScript),
    [buttonGroups, customScript]
  );
  const mappingFieldGroups = useMemo(
    () => makeAutomationMappingFieldGroups(configuration.fieldGroups, configuration.placements),
    [configuration.fieldGroups, configuration.placements]
  );
  const [step, setStep] = useState<ModalStep>('mapping');
  const [loadedFile, setLoadedFile] = useState<BrowserLoadedFile>();
  const [editState, setEditState] = useState<PdfEditorState>();
  const [mappedFields, setMappedFields] = useState<string[]>([]);
  const [reviewSource, setReviewSource] = useState<PdfEditorBrowserFileSource>();
  const [isPreparing, setIsPreparing] = useState(false);

  const prepareLoadedFile = useCallback(
    async (nextLoadedFile: BrowserLoadedFile) => {
      const authToken = await getAuthToken();
      if (!authToken) throw new Error('Unable to prepare PDF: authentication is not configured.');

      const preparedBytes = await preparePdfForEditing(authToken, nextLoadedFile.bytes);
      const preparedBuffer = preparedBytes.buffer.slice(
        preparedBytes.byteOffset,
        preparedBytes.byteOffset + preparedBytes.byteLength
      ) as ArrayBuffer;
      const preparedFile = new File([preparedBuffer], nextLoadedFile.fileName, {
        type: 'application/pdf',
        lastModified: nextLoadedFile.file.lastModified,
      });
      const preparedLoadedFile = {
        ...nextLoadedFile,
        file: preparedFile,
        blob: preparedFile,
        arrayBuffer: preparedBuffer,
        bytes: preparedBytes,
        fileSizeInBytes: preparedBytes.byteLength,
      };
      const imported = await importAutomationFields(
        preparedBytes,
        configuration.fieldGroups,
        configuration.placements
      );

      setLoadedFile(preparedLoadedFile);
      setEditState(imported.editState);
      setMappedFields(imported.logicalNames);
      setReviewSource(undefined);
      setStep('mapping');
    },
    [configuration.fieldGroups, configuration.placements, getAuthToken]
  );

  useEffect(
    () => () => {
      if (loadedFile?.objectUrl) URL.revokeObjectURL(loadedFile.objectUrl);
    },
    [loadedFile]
  );

  const handleFileLoaded = async (nextLoadedFile: BrowserLoadedFile) => {
    setIsPreparing(true);
    try {
      await prepareLoadedFile(nextLoadedFile);
    } catch {
      pushToast({ type: 'error', message: 'Unable to prepare Seller Disclosure PDF' });
    } finally {
      setIsPreparing(false);
    }
  };

  const handleEditStateChange = (nextEditState: PdfEditorState) => {
    setEditState(nextEditState);
    setMappedFields(getCompleteAutomationLogicalNames(configuration.fieldGroups, nextEditState));
  };

  const allRequiredFieldsMapped = requiredPdfFields.every((field) => mappedFields.includes(field));

  const handleMappingConfirmed = async () => {
    if (!loadedFile || !editState || !allRequiredFieldsMapped) return;
    setIsPreparing(true);
    try {
      const exported = await exportAutomationFields(
        loadedFile.bytes,
        configuration.fieldGroups,
        editState
      );
      setMappedFields(exported.logicalNames);
      setReviewSource({
        arrayBuffer: exported.pdfBytes.buffer as ArrayBuffer,
        fileName: loadedFile.fileName,
        documentKey: `${loadedFile.id}-review-${Date.now()}`,
      });
      setStep('compliance');
    } catch {
      pushToast({ type: 'error', message: 'Unable to prepare Seller Disclosure preview' });
    } finally {
      setIsPreparing(false);
    }
  };

  return {
    loadedFile,
    editState,
    mappedFields,
    reviewSource,
    mappingFieldGroups,
    isPreparing,
    allRequiredFieldsMapped,
    isMappingStep: step === 'mapping',
    handleFileLoaded,
    handleEditStateChange,
    handleMappingConfirmed,
    backToMapping: () => setStep('mapping'),
  };
};
